/**
 * Mesh shell utilities — floor removal and wall thickening (ported from V1).
 * Insertion axis ≈ +Z in aligned space.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import type { TrimHeightMap, TrimProfileCurve } from './alignerTrimField';
import { isColumnNearTrim, nearestTrimZ } from './alignerTrimField';

const INSERTION_AXIS = new Vector3(0, 0, 1);
// Relaxed so smoothed (near-vertical) floor faces are still treated as floor.
const FLOOR_NORMAL_DOT_THRESHOLD = 0.2;

/**
 * Open the gingival side of the shell along the trim curve.
 *
 * Two culls:
 *  - Absolute: any triangle whose centroid sits clearly below the per-column trim
 *    height is removed regardless of orientation — the aligner never extends past
 *    the trim line, so this reliably opens the bottom even after smoothing.
 *  - Floor: anti-insertion (downward) faces near the trim line are trimmed as a
 *    cleanup pass.
 */
export function removeTrimmedFloor(
  geometry: BufferGeometry,
  profile: TrimProfileCurve,
  floorToleranceMm = 1.5,
): BufferGeometry {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  if (!indexAttr || !positionAttr) return geometry;

  const indices = indexAttr.array;
  const newIndices: number[] = [];

  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  const centroid = new Vector3();
  const faceNormal = new Vector3();

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    pA.fromBufferAttribute(positionAttr, a);
    pB.fromBufferAttribute(positionAttr, b);
    pC.fromBufferAttribute(positionAttr, c);

    centroid.copy(pA).add(pB).add(pC).multiplyScalar(1 / 3);
    const curveZ = nearestTrimZ(profile, centroid.x, centroid.y);

    // Absolute below-trim cull: nothing belongs below the gingival margin.
    if (centroid.z < curveZ - floorToleranceMm) {
      continue;
    }

    faceNormal.copy(pB).sub(pA).cross(pC.clone().sub(pA)).normalize();
    const antiInsertion = faceNormal.dot(INSERTION_AXIS) < -FLOOR_NORMAL_DOT_THRESHOLD;
    if (antiInsertion && centroid.z < curveZ + floorToleranceMm) {
      continue;
    }

    newIndices.push(a, b, c);
  }

  geometry.setIndex(new Uint32BufferAttribute(newIndices, 1));
  return geometry;
}

/**
 * Open the gingival rim of the offset surface using the same smoothed per-column
 * trim height map that drove the SDF clip. Because the SDF clip already flattened
 * everything below the trim into a clean cap, a simple absolute "centroid below
 * trim height" test removes exactly that cap with no normal test (no speckle) and
 * no per-segment jitter. Geometry must be in aligned (insertion ≈ +Z) space, same
 * frame as the height map / grid.
 */
export function openShellBelowTrim(
  geometry: BufferGeometry,
  heightMap: TrimHeightMap,
  origin: { x: number; y: number },
  cellSize: number,
  toleranceMm = 0,
): BufferGeometry {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  if (!indexAttr || !positionAttr) return geometry;

  const { width, height, heights } = heightMap;
  const indices = indexAttr.array;
  const newIndices: number[] = [];

  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  const centroid = new Vector3();

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    pA.fromBufferAttribute(positionAttr, a);
    pB.fromBufferAttribute(positionAttr, b);
    pC.fromBufferAttribute(positionAttr, c);
    centroid.copy(pA).add(pB).add(pC).multiplyScalar(1 / 3);

    const ix = Math.min(width - 1, Math.max(0, Math.round((centroid.x - origin.x) / cellSize)));
    const iy = Math.min(height - 1, Math.max(0, Math.round((centroid.y - origin.y) / cellSize)));
    const trimZ = heights[iy * width + ix];

    if (Number.isFinite(trimZ) && centroid.z < trimZ + toleranceMm) {
      continue;
    }

    newIndices.push(a, b, c);
  }

  geometry.setIndex(new Uint32BufferAttribute(newIndices, 1));
  return geometry;
}

/**
 * Open the shell on the insertion (+Z) side of the trim curve (mesh clip, not SDF cliff).
 */
export function clipMeshAboveTrim(
  geometry: BufferGeometry,
  profile: TrimProfileCurve,
  trimMargin: number,
  voxelSize: number,
): { geometry: BufferGeometry; removedTriangles: number } {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  if (!indexAttr || !positionAttr) {
    return { geometry, removedTriangles: 0 };
  }

  const xyMargin = Math.max(trimMargin, voxelSize * 2);
  const indices = indexAttr.array;
  const newIndices: number[] = [];
  const centroid = new Vector3();
  let removedTriangles = 0;

  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i];
    const b = indices[i + 1];
    const c = indices[i + 2];

    centroid.set(0, 0, 0);
    centroid.fromBufferAttribute(positionAttr, a);
    const pB = new Vector3().fromBufferAttribute(positionAttr, b);
    const pC = new Vector3().fromBufferAttribute(positionAttr, c);
    centroid.add(pB).add(pC).multiplyScalar(1 / 3);

    if (isColumnNearTrim(centroid.x, centroid.y, profile, xyMargin)) {
      const trimZ = nearestTrimZ(profile, centroid.x, centroid.y);
      if (centroid.z > trimZ + trimMargin) {
        removedTriangles++;
        continue;
      }
    }

    newIndices.push(a, b, c);
  }

  geometry.setIndex(new Uint32BufferAttribute(newIndices, 1));
  return { geometry, removedTriangles };
}

/**
 * Smooth ONLY the shell's open bottom rim. The Taubin smoother pins boundary vertices so
 * the trim outline cannot creep — which also means the rim keeps its voxel-scale lateral
 * zig-zag. This pass fixes that specifically:
 *
 *   1. The boundary loop is smoothed as a 1D closed polyline (Taubin λ/μ so the outline
 *      does not shrink) — only vertices with exactly two boundary neighbours move.
 *   2. A thin band of near-rim vertices (graph distance ≤ bandRings) is relaxed with the
 *      same λ/μ scheme, boundary held fixed, so the wall blends into the new outline.
 *
 * Heights are NOT preserved here — the caller must re-project the returned boundary
 * vertices onto the trim cut surface afterwards (projectOntoCut) and re-run the one-sided
 * clamp for the band. Returns the boundary vertex indices for exactly that purpose.
 */
export function smoothShellRim(
  geometry: BufferGeometry,
  options: { loopIterations?: number; bandRings?: number; bandIterations?: number } = {},
): { boundaryIndices: number[] } {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position') as BufferAttribute | null;
  if (!indexAttr || !positionAttr) return { boundaryIndices: [] };
  const loopIterations = Math.max(0, options.loopIterations ?? 12);
  const bandRings = Math.max(0, options.bandRings ?? 3);
  const bandIterations = Math.max(0, options.bandIterations ?? 6);
  const vertexCount = positionAttr.count;
  const positions = positionAttr.array as Float32Array;

  // Unique undirected edges (numeric keys, see smoothSurface) + boundary detection.
  const edgeUse = new Map<number, number>();
  for (let i = 0; i < indexAttr.count; i += 3) {
    const a = indexAttr.getX(i);
    const b = indexAttr.getX(i + 1);
    const c = indexAttr.getX(i + 2);
    const k1 = a < b ? a * vertexCount + b : b * vertexCount + a;
    const k2 = b < c ? b * vertexCount + c : c * vertexCount + b;
    const k3 = c < a ? c * vertexCount + a : a * vertexCount + c;
    edgeUse.set(k1, (edgeUse.get(k1) ?? 0) + 1);
    edgeUse.set(k2, (edgeUse.get(k2) ?? 0) + 1);
    edgeUse.set(k3, (edgeUse.get(k3) ?? 0) + 1);
  }

  // Boundary loop adjacency (vertex → its neighbours along boundary edges only).
  const boundaryNeighbors = new Map<number, number[]>();
  edgeUse.forEach((count, key) => {
    if (count !== 1) return;
    const a = Math.floor(key / vertexCount);
    const b = key % vertexCount;
    (boundaryNeighbors.get(a) ?? boundaryNeighbors.set(a, []).get(a)!).push(b);
    (boundaryNeighbors.get(b) ?? boundaryNeighbors.set(b, []).get(b)!).push(a);
  });
  const boundaryIndices = [...boundaryNeighbors.keys()];
  if (!boundaryIndices.length) return { boundaryIndices };

  // 1) Taubin-smooth the boundary polyline (skip non-manifold junctions ≠ 2 neighbours).
  const loopPass = (factor: number) => {
    const updated: Array<[number, number, number, number]> = [];
    for (const vtx of boundaryIndices) {
      const nbs = boundaryNeighbors.get(vtx)!;
      if (nbs.length !== 2) continue;
      const s = vtx * 3;
      const n0 = nbs[0] * 3;
      const n1 = nbs[1] * 3;
      updated.push([
        vtx,
        positions[s] + factor * ((positions[n0] + positions[n1]) / 2 - positions[s]),
        positions[s + 1] + factor * ((positions[n0 + 1] + positions[n1 + 1]) / 2 - positions[s + 1]),
        positions[s + 2] + factor * ((positions[n0 + 2] + positions[n1 + 2]) / 2 - positions[s + 2]),
      ]);
    }
    for (const [vtx, x, y, z] of updated) {
      positions[vtx * 3] = x;
      positions[vtx * 3 + 1] = y;
      positions[vtx * 3 + 2] = z;
    }
  };
  for (let iter = 0; iter < loopIterations; iter++) {
    loopPass(0.5);
    loopPass(-0.53);
  }

  // 2) Blend band: BFS from the boundary through the full mesh adjacency.
  if (bandRings > 0 && bandIterations > 0) {
    const fullNeighbors = new Map<number, number[]>();
    edgeUse.forEach((_, key) => {
      const a = Math.floor(key / vertexCount);
      const b = key % vertexCount;
      (fullNeighbors.get(a) ?? fullNeighbors.set(a, []).get(a)!).push(b);
      (fullNeighbors.get(b) ?? fullNeighbors.set(b, []).get(b)!).push(a);
    });

    const isBoundary = new Set(boundaryIndices);
    const band = new Set<number>();
    let frontier = boundaryIndices;
    for (let ring = 0; ring < bandRings; ring++) {
      const next: number[] = [];
      for (const vtx of frontier) {
        for (const nb of fullNeighbors.get(vtx) ?? []) {
          if (isBoundary.has(nb) || band.has(nb)) continue;
          band.add(nb);
          next.push(nb);
        }
      }
      frontier = next;
    }

    const bandPass = (factor: number) => {
      const updated: Array<[number, number, number, number]> = [];
      for (const vtx of band) {
        const nbs = fullNeighbors.get(vtx)!;
        const s = vtx * 3;
        let ax = 0;
        let ay = 0;
        let az = 0;
        for (const nb of nbs) {
          const n = nb * 3;
          ax += positions[n];
          ay += positions[n + 1];
          az += positions[n + 2];
        }
        const inv = 1 / nbs.length;
        updated.push([
          vtx,
          positions[s] + factor * (ax * inv - positions[s]),
          positions[s + 1] + factor * (ay * inv - positions[s + 1]),
          positions[s + 2] + factor * (az * inv - positions[s + 2]),
        ]);
      }
      for (const [vtx, x, y, z] of updated) {
        positions[vtx * 3] = x;
        positions[vtx * 3 + 1] = y;
        positions[vtx * 3 + 2] = z;
      }
    };
    for (let iter = 0; iter < bandIterations; iter++) {
      bandPass(0.5);
      bandPass(-0.53);
    }
  }

  positionAttr.needsUpdate = true;
  return { boundaryIndices };
}

/**
 * Fast positions-only vertex weld: vertices whose coordinates quantize to the same
 * tolerance-sized cell are merged. Marching cubes emits bitwise-identical positions for
 * vertices shared across neighbouring cells, so exact-cell matching is sufficient.
 * Replaces three's mergeVertices on the aligner path — that helper builds a string hash
 * per vertex from every attribute, which dominates at marching-cubes vertex counts.
 * Non-position attributes are dropped (normals are recomputed downstream anyway).
 */
/**
 * Concatenates the position/index data of several geometries into one raw
 * geometry (all other attributes dropped). Non-indexed inputs are treated as
 * triangle soup. Typically followed by weldMeshVertices to join coincident
 * seams across the parts.
 */
/**
 * Splits every triangle whose longest edge exceeds `maxEdgeMm` (longest-edge
 * midpoint bisection, applied iteratively). Returns a NON-indexed position
 * soup — meant for consumers that don't care about topology, e.g. the SDF
 * boundary rasterizer, which needs no triangle to span more than ~a voxel.
 */
export function subdivideLongTriangles(geometry: BufferGeometry, maxEdgeMm: number): BufferGeometry {
  const positionAttr = geometry.getAttribute('position') as BufferAttribute;
  const indexAttr = geometry.getIndex();
  const triCount = (indexAttr ? indexAttr.count : positionAttr.count) / 3;
  const vertexOf = (t: number, k: number) => (indexAttr ? indexAttr.getX(t * 3 + k) : t * 3 + k);

  const out: number[] = [];
  const maxEdgeSq = maxEdgeMm * maxEdgeMm;
  const stack: Array<[Vector3, Vector3, Vector3]> = [];
  const emit = (a: Vector3, b: Vector3, c: Vector3) => {
    out.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  };

  for (let t = 0; t < triCount; t++) {
    stack.length = 0;
    stack.push([
      new Vector3().fromBufferAttribute(positionAttr, vertexOf(t, 0)),
      new Vector3().fromBufferAttribute(positionAttr, vertexOf(t, 1)),
      new Vector3().fromBufferAttribute(positionAttr, vertexOf(t, 2)),
    ]);
    // Depth guard: each bisection halves the longest edge, 24 levels is plenty.
    for (let guard = 0; guard < 1 << 24 && stack.length > 0; guard++) {
      const [a, b, c] = stack.pop()!;
      const ab = a.distanceToSquared(b);
      const bc = b.distanceToSquared(c);
      const ca = c.distanceToSquared(a);
      const longest = Math.max(ab, bc, ca);
      if (longest <= maxEdgeSq) {
        emit(a, b, c);
        continue;
      }
      if (longest === ab) {
        const m = a.clone().lerp(b, 0.5);
        stack.push([a, m, c], [m, b, c]);
      } else if (longest === bc) {
        const m = b.clone().lerp(c, 0.5);
        stack.push([a, b, m], [a, m, c]);
      } else {
        const m = c.clone().lerp(a, 0.5);
        stack.push([a, b, m], [m, b, c]);
      }
    }
  }

  const result = new BufferGeometry();
  result.setAttribute('position', new Float32BufferAttribute(out, 3));
  return result;
}

export function mergePositionIndexGeometries(geometries: BufferGeometry[]): BufferGeometry {
  let vertexTotal = 0;
  let indexTotal = 0;
  for (const g of geometries) {
    const pos = g.getAttribute('position') as BufferAttribute;
    vertexTotal += pos.count;
    indexTotal += g.getIndex() ? g.getIndex()!.count : pos.count;
  }

  const positions = new Float32Array(vertexTotal * 3);
  const indices = new Uint32Array(indexTotal);
  let vertexOffset = 0;
  let indexOffset = 0;
  for (const g of geometries) {
    const pos = g.getAttribute('position') as BufferAttribute;
    positions.set(pos.array as Float32Array, vertexOffset * 3);
    const index = g.getIndex();
    const count = index ? index.count : pos.count;
    for (let i = 0; i < count; i++) {
      indices[indexOffset + i] = (index ? index.getX(i) : i) + vertexOffset;
    }
    vertexOffset += pos.count;
    indexOffset += count;
  }

  const merged = new BufferGeometry();
  merged.setAttribute('position', new Float32BufferAttribute(positions, 3));
  merged.setIndex(new Uint32BufferAttribute(indices, 1));
  return merged;
}

/**
 * Fills every boundary loop EXCEPT the `keepLargest` longest ones (by
 * perimeter) with a fan. After merging gum + teeth and welding the seams, the
 * only legitimate boundary is the outer rim — anything else is a seam "zipper"
 * slit (gum and tooth vertices without an exact weld partner) that would read
 * as a rim for the thickener: pinned vertices, stitch quads and fold holes all
 * along the seam. Fan triangles are wound against the boundary edge direction
 * so they orient consistently with the surrounding surface.
 * Returns the number of loops filled.
 */
export function fillMinorBoundaryLoops(geometry: BufferGeometry, keepLargest = 1): number {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position') as BufferAttribute | null;
  if (!indexAttr || !positionAttr) return 0;
  const vertexCount = positionAttr.count;

  // Directed boundary edges: undirected use-count 1, direction as in its face.
  const use = new Map<number, number>();
  const dirA = new Map<number, number>();
  const dirB = new Map<number, number>();
  for (let i = 0; i < indexAttr.count; i += 3) {
    const tri = [indexAttr.getX(i), indexAttr.getX(i + 1), indexAttr.getX(i + 2)];
    for (let e = 0; e < 3; e++) {
      const a = tri[e];
      const b = tri[(e + 1) % 3];
      const key = a < b ? a * vertexCount + b : b * vertexCount + a;
      use.set(key, (use.get(key) ?? 0) + 1);
      if (!dirA.has(key)) {
        dirA.set(key, a);
        dirB.set(key, b);
      }
    }
  }

  const nextByStart = new Map<number, number[]>();
  use.forEach((count, key) => {
    if (count !== 1) return;
    const a = dirA.get(key)!;
    const b = dirB.get(key)!;
    if (!nextByStart.has(a)) nextByStart.set(a, []);
    nextByStart.get(a)!.push(b);
  });

  // Chain directed boundary edges into loops.
  const loops: number[][] = [];
  while (nextByStart.size > 0) {
    const [start] = nextByStart.keys();
    const loop: number[] = [start];
    let current = start;
    for (let guard = 0; guard < nextByStart.size + use.size; guard++) {
      const outs = nextByStart.get(current);
      if (!outs || outs.length === 0) {
        nextByStart.delete(current);
        break;
      }
      const next = outs.pop()!;
      if (outs.length === 0) nextByStart.delete(current);
      if (next === start) break;
      loop.push(next);
      current = next;
    }
    if (loop.length >= 3) loops.push(loop);
  }
  if (loops.length <= keepLargest) return 0;

  const perimeter = (loop: number[]) => {
    let sum = 0;
    const pa = new Vector3();
    const pb = new Vector3();
    for (let i = 0; i < loop.length; i++) {
      pa.fromArray(positionAttr.array as Float32Array, loop[i] * 3);
      pb.fromArray(positionAttr.array as Float32Array, loop[(i + 1) % loop.length] * 3);
      sum += pa.distanceTo(pb);
    }
    return sum;
  };
  loops.sort((a, b) => perimeter(b) - perimeter(a));

  const indices = Array.from(indexAttr.array as ArrayLike<number>);
  let filled = 0;
  for (let li = keepLargest; li < loops.length; li++) {
    const loop = loops[li];
    // Reversed fan: edge loop[i+1]→loop[i] opposes the boundary halfedge.
    for (let i = 1; i < loop.length - 1; i++) {
      indices.push(loop[0], loop[i + 1], loop[i]);
    }
    filled++;
  }
  if (filled > 0) geometry.setIndex(indices);
  return filled;
}

/**
 * Drops connected components smaller than `minTriangles` (voxel-scale debris:
 * isolated bubbles from doubled scan layers, cut slivers). The largest
 * component is always kept. Returns the number of components removed.
 */
export function removeSmallComponents(geometry: BufferGeometry, minTriangles: number): number {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  if (!indexAttr || !positionAttr) return 0;
  const vertexCount = positionAttr.count;
  const parent = new Int32Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) parent[i] = i;
  const find = (v: number): number => {
    let root = v;
    while (parent[root] !== root) root = parent[root];
    while (parent[v] !== root) {
      const next = parent[v];
      parent[v] = root;
      v = next;
    }
    return root;
  };
  for (let i = 0; i < indexAttr.count; i += 3) {
    const a = find(indexAttr.getX(i));
    parent[find(indexAttr.getX(i + 1))] = a;
    parent[find(indexAttr.getX(i + 2))] = a;
  }
  const triCount = new Map<number, number>();
  let largestRoot = -1;
  for (let i = 0; i < indexAttr.count; i += 3) {
    const root = find(indexAttr.getX(i));
    const count = (triCount.get(root) ?? 0) + 1;
    triCount.set(root, count);
    if (largestRoot < 0 || count > triCount.get(largestRoot)!) largestRoot = root;
  }
  let removed = 0;
  triCount.forEach((count, root) => {
    if (root !== largestRoot && count < minTriangles) removed++;
  });
  if (removed === 0) return 0;

  const kept: number[] = [];
  for (let i = 0; i < indexAttr.count; i += 3) {
    const root = find(indexAttr.getX(i));
    if (root === largestRoot || triCount.get(root)! >= minTriangles) {
      kept.push(indexAttr.getX(i), indexAttr.getX(i + 1), indexAttr.getX(i + 2));
    }
  }
  geometry.setIndex(kept);
  return removed;
}

export function weldMeshVertices(geometry: BufferGeometry, tolerance: number): BufferGeometry {
  const positionAttr = geometry.getAttribute('position') as BufferAttribute | null;
  if (!positionAttr) return geometry;
  const indexAttr = geometry.getIndex();
  const count = positionAttr.count;
  const inv = 1 / Math.max(tolerance, 1e-9);

  const buckets = new Map<number, number[]>(); // 32-bit spatial hash → welded vertex ids
  const remap = new Uint32Array(count);
  const outPositions: number[] = [];
  const qx: number[] = [];
  const qy: number[] = [];
  const qz: number[] = [];

  for (let i = 0; i < count; i++) {
    const x = positionAttr.getX(i);
    const y = positionAttr.getY(i);
    const z = positionAttr.getZ(i);
    const ix = Math.round(x * inv);
    const iy = Math.round(y * inv);
    const iz = Math.round(z * inv);
    // Hash collisions are resolved by exact quantized comparison inside the bucket.
    const hash = (Math.imul(ix, 73856093) ^ Math.imul(iy, 19349663) ^ Math.imul(iz, 83492791)) | 0;
    let bucket = buckets.get(hash);
    let found = -1;
    if (bucket) {
      for (const candidate of bucket) {
        if (qx[candidate] === ix && qy[candidate] === iy && qz[candidate] === iz) {
          found = candidate;
          break;
        }
      }
    } else {
      bucket = [];
      buckets.set(hash, bucket);
    }
    if (found >= 0) {
      remap[i] = found;
    } else {
      const id = outPositions.length / 3;
      outPositions.push(x, y, z);
      qx.push(ix);
      qy.push(iy);
      qz.push(iz);
      bucket.push(id);
      remap[i] = id;
    }
  }

  // Triangles collapsed by the weld (two or three corners fused — marching
  // cubes emits slivers whenever the iso passes near a grid node) are DROPPED:
  // zero-area faces with duplicated indices read as non-manifold garbage to
  // slicers, which "repair" them into holes.
  const srcIndexCount = indexAttr ? indexAttr.count : count;
  const outIndices: number[] = [];
  for (let i = 0; i < srcIndexCount; i += 3) {
    const a = remap[indexAttr ? indexAttr.getX(i) : i];
    const b = remap[indexAttr ? indexAttr.getX(i + 1) : i + 1];
    const c = remap[indexAttr ? indexAttr.getX(i + 2) : i + 2];
    if (a === b || b === c || c === a) continue;
    outIndices.push(a, b, c);
  }

  const welded = new BufferGeometry();
  welded.setAttribute('position', new Float32BufferAttribute(outPositions, 3));
  welded.setIndex(new Uint32BufferAttribute(new Uint32Array(outIndices), 1));
  return welded;
}

/**
 * Per-face flag: is this triangle part of a SHARP feature of the surface —
 * an edge whose dihedral angle exceeds `angleDeg`?
 *
 * On a scan this is what separates the two things a smoothing pass cannot tell
 * apart: real anatomy that must survive (cusp ridges, incisal edges, the
 * cervical crease, the crown/gum seam) versus 0.05–0.15 mm scanner roughness
 * that should be smoothed away. Blanket-reprojecting a smoothed shell onto the
 * scan restores BOTH — on a real jaw that moves ~40 % of all vertices and puts
 * the roughness straight back — so the projection is masked with this instead.
 *
 * Requires an indexed, welded geometry (run weldMeshVertices/fuseSeamTJunctions
 * first): dihedral angles are meaningless across unwelded duplicate vertices.
 */
export function markSharpFaces(
  geometry: BufferGeometry,
  angleDeg: number,
  dilateRings = 0,
): Uint8Array {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  const faceCount = indexAttr ? indexAttr.count / 3 : positionAttr.count / 3;
  const sharp = new Uint8Array(faceCount);
  if (!indexAttr) return sharp;

  const vertexCount = positionAttr.count;
  const normals = new Float32Array(faceCount * 3);
  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  const normal = new Vector3();
  for (let face = 0; face < faceCount; face++) {
    const i = face * 3;
    a.fromBufferAttribute(positionAttr, indexAttr.getX(i));
    b.fromBufferAttribute(positionAttr, indexAttr.getX(i + 1));
    c.fromBufferAttribute(positionAttr, indexAttr.getX(i + 2));
    normal.crossVectors(b.sub(a), c.sub(a));
    const length = normal.length();
    if (length > 1e-12) normal.multiplyScalar(1 / length);
    normals[i] = normal.x;
    normals[i + 1] = normal.y;
    normals[i + 2] = normal.z;
  }

  // First face per edge; the second one closes the pair and settles the angle.
  const firstFace = new Map<number, number>();
  const cosLimit = Math.cos((angleDeg * Math.PI) / 180);
  for (let face = 0; face < faceCount; face++) {
    const i = face * 3;
    for (let e = 0; e < 3; e++) {
      const u = indexAttr.getX(i + e);
      const v = indexAttr.getX(i + ((e + 1) % 3));
      const key = u < v ? u * vertexCount + v : v * vertexCount + u;
      const other = firstFace.get(key);
      if (other === undefined) {
        firstFace.set(key, face);
        continue;
      }
      const j = other * 3;
      const dot =
        normals[i] * normals[j] + normals[i + 1] * normals[j + 1] + normals[i + 2] * normals[j + 2];
      if (dot < cosLimit) {
        sharp[face] = 1;
        sharp[other] = 1;
      }
    }
  }

  // Grow the mask over the faces AROUND each feature. A projection only fixes
  // a shell vertex whose closest source triangle is masked, and the vertices
  // that carry a rounded-off crease sit a cell or two to either side of it —
  // with a bare crease mask most of them find an unmasked neighbour triangle
  // and stay where the smoothing left them.
  for (let ring = 0; ring < dilateRings; ring++) {
    const onFeature = new Uint8Array(vertexCount);
    for (let face = 0; face < faceCount; face++) {
      if (!sharp[face]) continue;
      const i = face * 3;
      onFeature[indexAttr.getX(i)] = 1;
      onFeature[indexAttr.getX(i + 1)] = 1;
      onFeature[indexAttr.getX(i + 2)] = 1;
    }
    const grown = Uint8Array.from(sharp);
    for (let face = 0; face < faceCount; face++) {
      if (sharp[face]) continue;
      const i = face * 3;
      if (
        onFeature[indexAttr.getX(i)] ||
        onFeature[indexAttr.getX(i + 1)] ||
        onFeature[indexAttr.getX(i + 2)]
      ) {
        grown[face] = 1;
      }
    }
    sharp.set(grown);
  }
  return sharp;
}

/**
 * Flags every vertex that sits in an INTERPROXIMAL CHANNEL: within `reachMm`
 * of TWO different crowns — the walls facing a gap narrower than ~2·reachMm.
 *
 * This is where a voxel model is at its worst (the segmentation seam and its
 * scan-bridge debris run through the channel, and 1–3-cell gaps render as
 * marching-cubes lace), and simultaneously where anatomy fidelity matters
 * least for a thermoformed appliance — the foil never pulls in. So the export
 * gives exactly this region MORE smoothing and NO reprojection, while cusps
 * and flats around it keep the normal treatment.
 *
 * Also returns a per-face mask (a face counts when any of its vertices do),
 * used to subtract the channel from the sharp-feature projection mask.
 */
export function markInterproximalRegion(
  geometry: BufferGeometry,
  crowns: BufferGeometry[],
  reachMm: number,
): { vertices: Uint8Array; faces: Uint8Array; flagged: number } {
  const positionAttr = geometry.getAttribute('position');
  const indexAttr = geometry.getIndex();
  const vertices = new Uint8Array(positionAttr.count);
  const faceCount = indexAttr ? indexAttr.count / 3 : positionAttr.count / 3;
  const faces = new Uint8Array(faceCount);
  let flagged = 0;
  if (crowns.length < 2 || reachMm <= 0) return { vertices, faces, flagged };

  const trees = crowns.map((crown) => {
    crown.computeBoundingBox();
    return { bvh: new MeshBVH(crown), box: crown.boundingBox! };
  });
  const probe = new Vector3();
  const hit = { point: new Vector3(), distance: 0, faceIndex: 0 };
  const target = hit as Parameters<MeshBVH['closestPointToPoint']>[1];
  const within = (tree: { bvh: MeshBVH; box: { min: Vector3; max: Vector3 } }): boolean => {
    const dx = Math.max(tree.box.min.x - probe.x, 0, probe.x - tree.box.max.x);
    const dy = Math.max(tree.box.min.y - probe.y, 0, probe.y - tree.box.max.y);
    const dz = Math.max(tree.box.min.z - probe.z, 0, probe.z - tree.box.max.z);
    if (dx * dx + dy * dy + dz * dz >= reachMm * reachMm) return false;
    if (!tree.bvh.closestPointToPoint(probe, target, 0, reachMm)) return false;
    return hit.distance <= reachMm; // maxThreshold only prunes BVH nodes
  };

  for (let v = 0; v < positionAttr.count; v++) {
    probe.fromBufferAttribute(positionAttr, v);
    let near = 0;
    for (const tree of trees) {
      if (!within(tree)) continue;
      near++;
      if (near === 2) break;
    }
    if (near >= 2) {
      vertices[v] = 1;
      flagged++;
    }
  }

  if (indexAttr) {
    for (let face = 0; face < faceCount; face++) {
      const i = face * 3;
      if (
        vertices[indexAttr.getX(i)] ||
        vertices[indexAttr.getX(i + 1)] ||
        vertices[indexAttr.getX(i + 2)]
      ) {
        faces[face] = 1;
      }
    }
  }
  return { vertices, faces, flagged };
}

export interface ProjectOntoSourceStats {
  /** Vertices pulled back onto the source surface. */
  moved: number;
  /** Vertices whose move was undone because it would have flipped a triangle. */
  reverted: number;
  /** Largest applied displacement (mm). */
  maxMoveMm: number;
}

/**
 * Pulls the shell's OUTER surface back onto the geometry it was built from.
 *
 * A voxel pipeline band-limits twice: marching cubes cuts every sharp feature
 * by a fraction of a cell, and Laplacian/Taubin smoothing — which cannot tell
 * a voxel terrace from a real crease — keeps eating the highest-curvature
 * places (cusp tips, incisal edges, the cervical crease). For a thermoformed
 * appliance that drift is what matters: the foil never reproduces a radius
 * below ~0.2 mm anyway, but it does care that the height of contour and the
 * gingival margin sit where the scan put them.
 *
 * So instead of trying to smooth less, this snaps every vertex that is ALREADY
 * within `bandMm` of the source onto its closest point there. Fidelity then
 * stops depending on the cell size (the error becomes the chord error of the
 * triangle sampling — microns on crown curvature) and the surface passes
 * through the original geometry again. Run it AFTER smoothing, ideally
 * interleaved with it, so each smoothing burst's drift is undone before it can
 * exceed the band.
 *
 * `bandMm` is what keeps the pass honest: vertices farther than that from the
 * source are deliberate new geometry — the inner wall of the hollow, the plane
 * cut at the bottom, interproximal blockout webs — and are left untouched.
 * Keep it around a cell size and well below half the wall thickness.
 *
 * Displacements are clamped to `maxStepMm`, and any move that would flip a
 * triangle is reverted (thin walls have near-coincident faces).
 */
export function projectOntoSource(
  geometry: BufferGeometry,
  source: MeshBVH,
  bandMm: number,
  maxStepMm: number,
  sourceFaceMask?: Uint8Array | null,
): ProjectOntoSourceStats {
  const positionAttr = geometry.getAttribute('position') as BufferAttribute | null;
  const indexAttr = geometry.getIndex();
  const stats: ProjectOntoSourceStats = { moved: 0, reverted: 0, maxMoveMm: 0 };
  if (!positionAttr || !indexAttr) return stats;

  const positions = positionAttr.array as Float32Array;
  const targets = Float32Array.from(positions);
  const probe = new Vector3();
  const hit = { point: new Vector3(), distance: 0, faceIndex: 0 };
  const target = hit as Parameters<MeshBVH['closestPointToPoint']>[1];
  const moved = new Uint8Array(positionAttr.count);

  for (let v = 0; v < positionAttr.count; v++) {
    const base = v * 3;
    probe.set(positions[base], positions[base + 1], positions[base + 2]);
    // maxThreshold only prunes BVH NODES, so a returned hit can still be
    // farther than the band (the closest triangle inside an accepted node).
    // Re-check explicitly: without it the pass grabs inner-wall vertices and
    // drags them towards the outer surface, thinning the shell.
    if (!source.closestPointToPoint(probe, target, 0, bandMm)) continue;
    if (hit.distance > bandMm) continue;
    // Feature-masked mode: only snap back to sharp source features, so the
    // pass restores anatomy without restoring the scan roughness the smoothing
    // was there to remove.
    if (sourceFaceMask && !sourceFaceMask[hit.faceIndex]) continue;
    let dx = hit.point.x - probe.x;
    let dy = hit.point.y - probe.y;
    let dz = hit.point.z - probe.z;
    const length = Math.hypot(dx, dy, dz);
    if (length < 1e-9) continue;
    if (length > maxStepMm) {
      const scale = maxStepMm / length;
      dx *= scale;
      dy *= scale;
      dz *= scale;
    }
    targets[base] = probe.x + dx;
    targets[base + 1] = probe.y + dy;
    targets[base + 2] = probe.z + dz;
    moved[v] = 1;
    stats.moved++;
    stats.maxMoveMm = Math.max(stats.maxMoveMm, Math.min(length, maxStepMm));
  }

  if (stats.moved === 0) return stats;

  // Flip guard: a move is only safe if every incident triangle keeps its
  // orientation. Two passes — reverting one vertex changes its neighbours'
  // verdict, and the reverted state is always valid (it is the input).
  const normalBefore = new Vector3();
  const normalAfter = new Vector3();
  const edge1 = new Vector3();
  const edge2 = new Vector3();
  const faceNormal = (data: Float32Array, a: number, b: number, c: number, out: Vector3) => {
    edge1.set(data[b] - data[a], data[b + 1] - data[a + 1], data[b + 2] - data[a + 2]);
    edge2.set(data[c] - data[a], data[c + 1] - data[a + 1], data[c + 2] - data[a + 2]);
    out.crossVectors(edge1, edge2);
  };
  for (let pass = 0; pass < 2; pass++) {
    let reverted = 0;
    for (let i = 0; i < indexAttr.count; i += 3) {
      const ia = indexAttr.getX(i);
      const ib = indexAttr.getX(i + 1);
      const ic = indexAttr.getX(i + 2);
      if (!moved[ia] && !moved[ib] && !moved[ic]) continue;
      const a = ia * 3;
      const b = ib * 3;
      const c = ic * 3;
      faceNormal(positions, a, b, c, normalBefore);
      faceNormal(targets, a, b, c, normalAfter);
      if (normalBefore.dot(normalAfter) > 0) continue;
      for (const idx of [ia, ib, ic]) {
        if (!moved[idx]) continue;
        const base = idx * 3;
        targets[base] = positions[base];
        targets[base + 1] = positions[base + 1];
        targets[base + 2] = positions[base + 2];
        moved[idx] = 0;
        reverted++;
      }
    }
    stats.reverted += reverted;
    stats.moved -= reverted;
    if (reverted === 0) break;
  }

  positions.set(targets);
  positionAttr.needsUpdate = true;
  geometry.computeVertexNormals();
  return stats;
}

/**
 * Taubin (lambda/mu) smoothing: a positive Laplacian pass followed by a negative
 * pass cancels the volume shrinkage of plain Laplacian smoothing while still
 * removing marching-cubes terracing. Requires an indexed geometry with shared
 * vertices (run a vertex weld first). Boundary rim vertices are kept fixed so the
 * trim edge does not creep. `maxStepMm` caps the per-pass vertex displacement —
 * on a thin shell the two wall faces are near-coincident and an unbounded
 * Laplacian step can push them through each other (flipped slivers / pinholes),
 * so callers should cap the step to a fraction of the wall thickness.
 *
 * `vertexMask` restricts the smoothing to the flagged vertices; everything
 * else acts as a fixed anchor. Used to give a specific region — the
 * interproximal channels — extra passes without touching the rest.
 */
export function smoothSurface(
  geometry: BufferGeometry,
  iterations = 8,
  lambda = 0.5,
  mu = -0.53,
  maxStepMm = Infinity,
  vertexMask?: Uint8Array | null,
): BufferGeometry {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position') as BufferAttribute | null;
  if (iterations <= 0 || !indexAttr || !positionAttr) {
    return geometry;
  }

  const vertexCount = positionAttr.count;

  // Unique undirected edges with NUMERIC keys (min·V + max, exact below 2^53) — string
  // keys and array-includes adjacency are GC-heavy at marching-cubes vertex counts.
  const edgeUse = new Map<number, number>();
  for (let i = 0; i < indexAttr.count; i += 3) {
    const a = indexAttr.getX(i);
    const b = indexAttr.getX(i + 1);
    const c = indexAttr.getX(i + 2);
    const k1 = a < b ? a * vertexCount + b : b * vertexCount + a;
    const k2 = b < c ? b * vertexCount + c : c * vertexCount + b;
    const k3 = c < a ? c * vertexCount + a : a * vertexCount + c;
    edgeUse.set(k1, (edgeUse.get(k1) ?? 0) + 1);
    edgeUse.set(k2, (edgeUse.get(k2) ?? 0) + 1);
    edgeUse.set(k3, (edgeUse.get(k3) ?? 0) + 1);
  }

  // CSR adjacency from the unique edges. Vertices on a boundary (rim) edge — an edge
  // used by exactly one triangle — stay pinned so the trim outline holds.
  const degree = new Uint32Array(vertexCount);
  const pinned = new Uint8Array(vertexCount);
  edgeUse.forEach((count, key) => {
    const a = Math.floor(key / vertexCount);
    const b = key % vertexCount;
    degree[a]++;
    degree[b]++;
    if (count === 1) {
      pinned[a] = 1;
      pinned[b] = 1;
    }
  });
  const adjacencyOffsets = new Uint32Array(vertexCount + 1);
  for (let v = 0; v < vertexCount; v++) {
    adjacencyOffsets[v + 1] = adjacencyOffsets[v] + degree[v];
  }
  const adjacency = new Uint32Array(adjacencyOffsets[vertexCount]);
  const cursor = adjacencyOffsets.slice(0, vertexCount);
  edgeUse.forEach((_, key) => {
    const a = Math.floor(key / vertexCount);
    const b = key % vertexCount;
    adjacency[cursor[a]++] = b;
    adjacency[cursor[b]++] = a;
  });

  const positions = positionAttr.array as Float32Array;
  const buffer = new Float32Array(positions.length);

  const applyPass = (factor: number) => {
    for (let v = 0; v < vertexCount; v++) {
      const start = v * 3;
      const nStart = adjacencyOffsets[v];
      const nEnd = adjacencyOffsets[v + 1];
      if (pinned[v] || nEnd === nStart || (vertexMask && !vertexMask[v])) {
        buffer[start] = positions[start];
        buffer[start + 1] = positions[start + 1];
        buffer[start + 2] = positions[start + 2];
        continue;
      }
      let avgX = 0;
      let avgY = 0;
      let avgZ = 0;
      for (let n = nStart; n < nEnd; n++) {
        const ni = adjacency[n] * 3;
        avgX += positions[ni];
        avgY += positions[ni + 1];
        avgZ += positions[ni + 2];
      }
      const inv = 1 / (nEnd - nStart);
      let dx = factor * (avgX * inv - positions[start]);
      let dy = factor * (avgY * inv - positions[start + 1]);
      let dz = factor * (avgZ * inv - positions[start + 2]);
      if (maxStepMm !== Infinity) {
        const len = Math.hypot(dx, dy, dz);
        if (len > maxStepMm) {
          const scale = maxStepMm / len;
          dx *= scale;
          dy *= scale;
          dz *= scale;
        }
      }
      buffer[start] = positions[start] + dx;
      buffer[start + 1] = positions[start + 1] + dy;
      buffer[start + 2] = positions[start + 2] + dz;
    }
    positions.set(buffer);
  };

  for (let iter = 0; iter < iterations; iter++) {
    applyPass(lambda);
    applyPass(mu);
  }

  positionAttr.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

/** Unique undirected edges + boundary-vertex flags (edge used by one triangle = rim). */
function buildEdgeTopology(indexAttr: BufferAttribute, vertexCount: number) {
  // Numeric keys (exact below 2^53), same trick as smoothSurface.
  const edgeUse = new Map<number, number>();
  for (let i = 0; i < indexAttr.count; i += 3) {
    const a = indexAttr.getX(i);
    const b = indexAttr.getX(i + 1);
    const c = indexAttr.getX(i + 2);
    const k1 = a < b ? a * vertexCount + b : b * vertexCount + a;
    const k2 = b < c ? b * vertexCount + c : c * vertexCount + b;
    const k3 = c < a ? c * vertexCount + a : a * vertexCount + c;
    edgeUse.set(k1, (edgeUse.get(k1) ?? 0) + 1);
    edgeUse.set(k2, (edgeUse.get(k2) ?? 0) + 1);
    edgeUse.set(k3, (edgeUse.get(k3) ?? 0) + 1);
  }
  const pinned = new Uint8Array(vertexCount);
  // Up to two rim neighbours per boundary vertex (manifold-with-boundary rims
  // have exactly two); −1 = unset.
  const rimNeighbor1 = new Int32Array(vertexCount).fill(-1);
  const rimNeighbor2 = new Int32Array(vertexCount).fill(-1);
  const addRimNeighbor = (v: number, n: number) => {
    if (rimNeighbor1[v] === -1) rimNeighbor1[v] = n;
    else if (rimNeighbor2[v] === -1) rimNeighbor2[v] = n;
  };
  edgeUse.forEach((count, key) => {
    if (count !== 1) return;
    const a = Math.floor(key / vertexCount);
    const b = key % vertexCount;
    pinned[a] = 1;
    pinned[b] = 1;
    addRimNeighbor(a, b);
    addRimNeighbor(b, a);
  });
  return { edgeUse, pinned, rimNeighbor1, rimNeighbor2 };
}

/**
 * Damped uniform Laplacian smoothing (Jacobi, λ per pass). Interior vertices
 * average over all neighbours; rim vertices average only along the rim
 * polyline (their two boundary neighbours) — they stay on the rim curve but
 * the smoothed interior meets them without a hard pinned/smoothed step that
 * would fold the subsequent normal offset.
 */
function smoothPositionsPinned(
  edgeUse: Map<number, number>,
  pinned: Uint8Array,
  rimNeighbor1: Int32Array,
  rimNeighbor2: Int32Array,
  positions: Float32Array,
  vertexCount: number,
  iterations: number,
  lambda = 0.5,
): void {
  // Flatten the edge map once — iterating a Map per pass is the hot spot.
  const edgeCount = edgeUse.size;
  const edgeA = new Uint32Array(edgeCount);
  const edgeB = new Uint32Array(edgeCount);
  let e = 0;
  edgeUse.forEach((_, key) => {
    edgeA[e] = Math.floor(key / vertexCount);
    edgeB[e] = key % vertexCount;
    e++;
  });

  const accum = new Float32Array(vertexCount * 3);
  const degree = new Float32Array(vertexCount);

  for (let iter = 0; iter < iterations; iter++) {
    accum.fill(0);
    degree.fill(0);
    for (let i = 0; i < edgeCount; i++) {
      const a3 = edgeA[i] * 3;
      const b3 = edgeB[i] * 3;
      accum[a3] += positions[b3];
      accum[a3 + 1] += positions[b3 + 1];
      accum[a3 + 2] += positions[b3 + 2];
      accum[b3] += positions[a3];
      accum[b3 + 1] += positions[a3 + 1];
      accum[b3 + 2] += positions[a3 + 2];
      degree[edgeA[i]]++;
      degree[edgeB[i]]++;
    }
    for (let v = 0; v < vertexCount; v++) {
      const v3 = v * 3;
      if (pinned[v]) {
        // Rim vertex: relax toward the midpoint of its two rim neighbours only,
        // and gently — full-strength polyline smoothing contracts the rim at
        // high-curvature stretches (distal ends of the arch), which stretches
        // the stitch band into long fans.
        const r1 = rimNeighbor1[v];
        const r2 = rimNeighbor2[v];
        if (r1 === -1 || r2 === -1) continue;
        const rimLambda = lambda * 0.2;
        const r13 = r1 * 3;
        const r23 = r2 * 3;
        positions[v3] += rimLambda * ((positions[r13] + positions[r23]) * 0.5 - positions[v3]);
        positions[v3 + 1] += rimLambda * ((positions[r13 + 1] + positions[r23 + 1]) * 0.5 - positions[v3 + 1]);
        positions[v3 + 2] += rimLambda * ((positions[r13 + 2] + positions[r23 + 2]) * 0.5 - positions[v3 + 2]);
        continue;
      }
      if (degree[v] === 0) continue;
      const inv = 1 / degree[v];
      positions[v3] += lambda * (accum[v3] * inv - positions[v3]);
      positions[v3 + 1] += lambda * (accum[v3 + 1] * inv - positions[v3 + 1]);
      positions[v3 + 2] += lambda * (accum[v3 + 2] * inv - positions[v3 + 2]);
    }
  }
}

/** Area-weighted per-vertex normals computed from an arbitrary position buffer. */
function vertexNormalsFromPositions(
  indexAttr: BufferAttribute,
  positions: Float32Array,
  vertexCount: number,
): Float32Array {
  const normals = new Float32Array(vertexCount * 3);
  const ab = new Vector3();
  const ac = new Vector3();
  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  for (let i = 0; i < indexAttr.count; i += 3) {
    const a = indexAttr.getX(i);
    const b = indexAttr.getX(i + 1);
    const c = indexAttr.getX(i + 2);
    pA.fromArray(positions, a * 3);
    pB.fromArray(positions, b * 3);
    pC.fromArray(positions, c * 3);
    ab.subVectors(pB, pA);
    ac.subVectors(pC, pA);
    ab.cross(ac); // area-weighted face normal
    for (const v of [a, b, c]) {
      normals[v * 3] += ab.x;
      normals[v * 3 + 1] += ab.y;
      normals[v * 3 + 2] += ab.z;
    }
  }
  for (let v = 0; v < vertexCount; v++) {
    const v3 = v * 3;
    const len = Math.hypot(normals[v3], normals[v3 + 1], normals[v3 + 2]);
    if (len > 1e-12) {
      normals[v3] /= len;
      normals[v3 + 1] /= len;
      normals[v3 + 2] /= len;
    }
  }
  return normals;
}

/**
 * Untangles fold-overs in the inner offset copy: triangles whose inner mirror
 * flipped against (or collapsed relative to) their outer counterpart flag their
 * vertices, and the flagged non-rim vertices relax toward their neighbour
 * average until the folds disappear (or the round budget runs out). Keeps the
 * inner wall closed where a drop-only policy would leave holes at seam creases.
 */
function repairFoldedInner(
  indexAttr: BufferAttribute,
  outerPositions: Float32Array,
  innerPositions: Float32Array,
  vertexCount: number,
  edgeUse: Map<number, number>,
  pinned: Uint8Array,
): void {
  const edgeCount = edgeUse.size;
  const edgeA = new Uint32Array(edgeCount);
  const edgeB = new Uint32Array(edgeCount);
  let e = 0;
  edgeUse.forEach((_, key) => {
    edgeA[e] = Math.floor(key / vertexCount);
    edgeB[e] = key % vertexCount;
    e++;
  });

  const outerArea = new Vector3();
  const innerArea = new Vector3();
  const tA = new Vector3();
  const tB = new Vector3();
  const tC = new Vector3();
  const areaVec = (a: number, b: number, c: number, positions: Float32Array, out: Vector3) => {
    tA.fromArray(positions, a * 3);
    tB.fromArray(positions, b * 3);
    tC.fromArray(positions, c * 3);
    out.subVectors(tB, tA).cross(tC.clone().sub(tA));
  };

  const flagged = new Uint8Array(vertexCount);
  const everFlagged = new Uint8Array(vertexCount);
  const accum = new Float32Array(vertexCount * 3);
  const degree = new Float32Array(vertexCount);

  // Flags every vertex of a fold-broken inner triangle; returns the count.
  const detectFolds = (): number => {
    flagged.fill(0);
    let foldCount = 0;
    for (let i = 0; i < indexAttr.count; i += 3) {
      const a = indexAttr.getX(i);
      const b = indexAttr.getX(i + 1);
      const c = indexAttr.getX(i + 2);
      areaVec(a, b, c, outerPositions, outerArea);
      areaVec(a, b, c, innerPositions, innerArea);
      if (innerArea.dot(outerArea) < 0 || innerArea.lengthSq() < outerArea.lengthSq() * 1e-4) {
        flagged[a] = 1;
        flagged[b] = 1;
        flagged[c] = 1;
        everFlagged[a] = 1;
        everFlagged[b] = 1;
        everFlagged[c] = 1;
        foldCount++;
      }
    }
    return foldCount;
  };

  // Damped Laplacian step applied to the vertices selected by `mask`.
  const relaxMasked = (mask: Uint8Array, factorFree: number, factorPinned: number) => {
    accum.fill(0);
    degree.fill(0);
    for (let i = 0; i < edgeCount; i++) {
      const a3 = edgeA[i] * 3;
      const b3 = edgeB[i] * 3;
      accum[a3] += innerPositions[b3];
      accum[a3 + 1] += innerPositions[b3 + 1];
      accum[a3 + 2] += innerPositions[b3 + 2];
      accum[b3] += innerPositions[a3];
      accum[b3 + 1] += innerPositions[a3 + 1];
      accum[b3 + 2] += innerPositions[a3 + 2];
      degree[edgeA[i]]++;
      degree[edgeB[i]]++;
    }
    for (let v = 0; v < vertexCount; v++) {
      if (!mask[v] || degree[v] === 0) continue;
      const factor = pinned[v] ? factorPinned : factorFree;
      const v3 = v * 3;
      const inv = 1 / degree[v];
      innerPositions[v3] += factor * (accum[v3] * inv - innerPositions[v3]);
      innerPositions[v3 + 1] += factor * (accum[v3 + 1] * inv - innerPositions[v3 + 1]);
      innerPositions[v3 + 2] += factor * (accum[v3 + 2] * inv - innerPositions[v3 + 2]);
    }
  };

  // Rim vertices may move too (the stitch band just tilts) — but gently,
  // so an untangle next to the rim cannot deform the band outline much.
  const maxRounds = 40;
  let clean = false;
  for (let round = 0; round < maxRounds; round++) {
    if (detectFolds() === 0) {
      clean = true;
      break;
    }
    relaxMasked(flagged, 0.7, 0.35);
  }

  if (!clean) {
    // Residual folds survived every relaxation round (the sharpest seam
    // creases). Pull the involved inner vertices toward their outer
    // counterparts — as inner → outer the mirrored triangle converges to
    // the outer one, which is never flipped. The wall gets locally thinner
    // at those few triangles, but the inner surface stays CLOSED, where
    // dropping the faces would leave holes.
    for (let round = 0; round < 12; round++) {
      if (detectFolds() === 0) break;
      for (let v = 0; v < vertexCount; v++) {
        if (!flagged[v]) continue;
        const v3 = v * 3;
        innerPositions[v3] += (outerPositions[v3] - innerPositions[v3]) * 0.5;
        innerPositions[v3 + 1] += (outerPositions[v3 + 1] - innerPositions[v3 + 1]) * 0.5;
        innerPositions[v3 + 2] += (outerPositions[v3 + 2] - innerPositions[v3 + 2]) * 0.5;
      }
    }
  }
  if (everFlagged.every((f) => f === 0)) return;

  // IRON the crease neighbourhood: the pulls above leave a sawtooth of
  // full-depth vertices next to surface-snapped ones. Dilate the ever-broken
  // zone a few rings and alternate smoothing with fold fix-ups, so the
  // resolved crease reads as one gently bridged valley instead of spikes.
  for (let ring = 0; ring < 3; ring++) {
    const grow = new Uint8Array(vertexCount);
    for (let i = 0; i < edgeCount; i++) {
      if (everFlagged[edgeA[i]]) grow[edgeB[i]] = 1;
      if (everFlagged[edgeB[i]]) grow[edgeA[i]] = 1;
    }
    for (let v = 0; v < vertexCount; v++) everFlagged[v] |= grow[v];
  }
  for (let iter = 0; iter < 20; iter++) {
    relaxMasked(everFlagged, 0.5, 0.2);
    if (detectFolds() === 0) continue;
    for (let v = 0; v < vertexCount; v++) {
      if (!flagged[v]) continue;
      const v3 = v * 3;
      innerPositions[v3] += (outerPositions[v3] - innerPositions[v3]) * 0.5;
      innerPositions[v3 + 1] += (outerPositions[v3 + 1] - innerPositions[v3 + 1]) * 0.5;
      innerPositions[v3 + 2] += (outerPositions[v3 + 2] - innerPositions[v3 + 2]) * 0.5;
    }
  }
  // Hard snap for any stragglers — a zero-thickness wall beats a hole.
  if (detectFolds() > 0) {
    for (let v = 0; v < vertexCount; v++) {
      if (!flagged[v]) continue;
      const v3 = v * 3;
      innerPositions[v3] = outerPositions[v3];
      innerPositions[v3 + 1] = outerPositions[v3 + 1];
      innerPositions[v3 + 2] = outerPositions[v3 + 2];
    }
  }
}

/**
 * Offset inner surface inward by thickness and stitch open boundary quads.
 *
 * With `innerSmoothIterations > 0` the inner copy is built smooth-first: the
 * surface is Laplacian-smoothed (rim vertices pinned), normals are recomputed
 * from the SMOOTHED shape, and only then offset inward. Offsetting after
 * smoothing means tight concavities (occlusal fossae, interdental valleys) no
 * longer fold the offset over itself — the inner side comes out as a clean
 * low-poly-looking shell while the outer keeps full detail. With 0 iterations
 * the legacy raw per-vertex-normal offset is used.
 *
 * The offset direction self-corrects: if the mesh winding makes normals point
 * into the body (global flip), the offset sign is inverted so the inner copy
 * always lands inside.
 */
export function thickenSurface(
  outerGeometry: BufferGeometry,
  thicknessMm: number,
  maxRimEdgeLengthMm = 3,
  innerSmoothIterations = 0,
): BufferGeometry {
  const positionAttr = outerGeometry.getAttribute('position') as BufferAttribute;
  const normalAttr = outerGeometry.getAttribute('normal') as BufferAttribute;
  const outerIndices = outerGeometry.getIndex();

  if (!outerIndices) {
    throw new Error('thickenSurface: outer geometry must be indexed.');
  }

  const vertexCount = positionAttr.count;
  const outerPositions = positionAttr.array as Float32Array;
  const innerPositions = new Float32Array(outerPositions.length);
  innerPositions.set(outerPositions);

  // Global orientation guard: normals should point AWAY from the body so that
  // `pos − n·t` lands inside. Vote with Σ dot(n_v, p_v − centroid); a negative
  // sum means the winding (and thus every normal) is globally flipped.
  let cx = 0;
  let cy = 0;
  let cz = 0;
  for (let i = 0; i < vertexCount; i++) {
    cx += outerPositions[i * 3];
    cy += outerPositions[i * 3 + 1];
    cz += outerPositions[i * 3 + 2];
  }
  cx /= vertexCount;
  cy /= vertexCount;
  cz /= vertexCount;
  let orientationVote = 0;
  for (let i = 0; i < vertexCount; i++) {
    orientationVote +=
      normalAttr.getX(i) * (outerPositions[i * 3] - cx) +
      normalAttr.getY(i) * (outerPositions[i * 3 + 1] - cy) +
      normalAttr.getZ(i) * (outerPositions[i * 3 + 2] - cz);
  }
  const offsetSign = orientationVote < 0 ? 1 : -1;

  if (innerSmoothIterations > 0) {
    const { edgeUse, pinned, rimNeighbor1, rimNeighbor2 } = buildEdgeTopology(outerIndices, vertexCount);
    smoothPositionsPinned(
      edgeUse,
      pinned,
      rimNeighbor1,
      rimNeighbor2,
      innerPositions,
      vertexCount,
      innerSmoothIterations,
    );
    const smoothedNormals = vertexNormalsFromPositions(outerIndices, innerPositions, vertexCount);
    for (let i = 0; i < vertexCount; i++) {
      innerPositions[i * 3 + 0] += offsetSign * smoothedNormals[i * 3 + 0] * thicknessMm;
      innerPositions[i * 3 + 1] += offsetSign * smoothedNormals[i * 3 + 1] * thicknessMm;
      innerPositions[i * 3 + 2] += offsetSign * smoothedNormals[i * 3 + 2] * thicknessMm;
    }
    // Sharp creases (gum↔tooth seams) still fold the offset locally even after
    // smoothing — instead of dropping those faces (holes), relax the involved
    // vertices until the folds untangle.
    repairFoldedInner(outerIndices, outerPositions, innerPositions, vertexCount, edgeUse, pinned);
  } else {
    for (let i = 0; i < vertexCount; i++) {
      innerPositions[i * 3 + 0] += offsetSign * normalAttr.getX(i) * thicknessMm;
      innerPositions[i * 3 + 1] += offsetSign * normalAttr.getY(i) * thicknessMm;
      innerPositions[i * 3 + 2] += offsetSign * normalAttr.getZ(i) * thicknessMm;
    }
  }

  const finalPositions = new Float32Array(vertexCount * 6);
  finalPositions.set(outerPositions, 0);
  finalPositions.set(innerPositions, outerPositions.length);

  const finalIndices: number[] = [];
  const edgeUsage = new Map<string, { a: number; b: number; count: number }>();

  const registerEdge = (a: number, b: number) => {
    const key = a < b ? `${a}_${b}` : `${b}_${a}`;
    const existing = edgeUsage.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      edgeUsage.set(key, { a, b, count: 1 });
    }
  };

  // The outer surface is ALWAYS kept intact — it is the visible/detailed side.
  // In the smooth-first path the inner copy is kept COMPLETE as well:
  // repairFoldedInner resolves every fold (worst case by locally thinning the
  // wall to zero), so dropping faces — which left holes along the gum↔tooth
  // seam creases — is no longer needed. Only the legacy raw-offset path still
  // drops inner mirrors that collapsed (area sliver) or folded over (winding
  // flipped against the outer face); the test is RELATIVE to the outer
  // triangle — an absolute area floor would wipe out most triangles of a
  // dense scan mesh and leave confetti.
  const keepAllInner = innerSmoothIterations > 0;
  const triA = new Vector3();
  const triB = new Vector3();
  const triC = new Vector3();
  const areaVec = new Vector3();

  const triAreaVec = (a: number, b: number, c: number, positions: Float32Array, out: Vector3) => {
    triA.fromArray(positions, a * 3);
    triB.fromArray(positions, b * 3);
    triC.fromArray(positions, c * 3);
    out.subVectors(triB, triA).cross(triC.clone().sub(triA));
  };

  const outerAreaVec = new Vector3();
  const minInnerAreaRatioSq = 1e-4; // inner area below 1% of outer = collapsed

  for (let i = 0; i < outerIndices.count; i += 3) {
    const a = outerIndices.getX(i);
    const b = outerIndices.getX(i + 1);
    const c = outerIndices.getX(i + 2);

    finalIndices.push(a, b, c);
    registerEdge(a, b);
    registerEdge(b, c);
    registerEdge(c, a);

    triAreaVec(a, b, c, outerPositions, outerAreaVec);
    triAreaVec(a, b, c, innerPositions, areaVec);
    const flipped = areaVec.dot(outerAreaVec) < 0;
    const collapsed = areaVec.lengthSq() < outerAreaVec.lengthSq() * minInnerAreaRatioSq;
    if (keepAllInner || (!flipped && !collapsed)) {
      finalIndices.push(c + vertexCount, b + vertexCount, a + vertexCount);
    }
  }

  const pa = new Vector3();
  const pb = new Vector3();
  const paInner = new Vector3();
  const pbInner = new Vector3();
  const maxRimEdgeLengthSq = maxRimEdgeLengthMm * maxRimEdgeLengthMm;

  edgeUsage.forEach((edge) => {
    if (edge.count !== 1) return;
    const { a, b } = edge;
    pa.fromArray(outerPositions, a * 3);
    pb.fromArray(outerPositions, b * 3);
    if (pa.distanceToSquared(pb) > maxRimEdgeLengthSq) return;

    // Skip rim quads where the inner edge has collapsed/crossed (degenerate wall).
    paInner.fromArray(innerPositions, a * 3);
    pbInner.fromArray(innerPositions, b * 3);
    if (paInner.distanceToSquared(pbInner) < 1e-8) return;

    const innerA = a + vertexCount;
    const innerB = b + vertexCount;
    // The wall must traverse the boundary edge OPPOSITE to the outer face's
    // a→b direction, otherwise its normal points into the shell and the band
    // renders backface-culled (invisible) from outside.
    finalIndices.push(b, a, innerA);
    finalIndices.push(b, innerA, innerB);
  });

  const solidGeometry = new BufferGeometry();
  solidGeometry.setAttribute('position', new Float32BufferAttribute(finalPositions, 3));
  solidGeometry.setIndex(finalIndices);
  solidGeometry.computeVertexNormals();
  return solidGeometry;
}
